import * as React from 'react'

import { TableComponent } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'
import { ProjectionKind } from 'generated/resources/datatable.graphql'

import Body from 'modules/core/components/DataTable/components/Body'
import EmptyState from 'modules/core/components/DataTable/components/EmptyState'
import Error from 'modules/core/components/DataTable/components/Error'
import Head from 'modules/core/components/DataTable/components/Head'
import Pagination from 'modules/core/components/DataTable/components/Pagination'
import Skeleton from 'modules/core/components/DataTable/components/Skeleton'
import { PAGINATION_KIND } from 'modules/core/components/DataTable/constants'
import useData from 'modules/core/components/DataTable/hooks/useData'
import useHeader from 'modules/core/components/DataTable/hooks/useHeader'
import useLoading from 'modules/core/components/DataTable/hooks/useLoading'
import usePagination from 'modules/core/components/DataTable/hooks/usePagination'
import useProjections from 'modules/core/components/DataTable/hooks/useProjections'
import { useEffect } from 'modules/core/hooks/useEffect'

import { Selectable } from './types'

export interface TableProps {
  density?: React.ComponentProps<typeof TableComponent>['density']
  emptyState?: React.ReactNode
  selectable?: Selectable
  noTopRadius?: boolean
  autoWidth?: boolean
}

// TODO: Test this with a lot of data and memoize stuff as much as possible
function Table<T, S extends string>({
  density,
  emptyState,
  selectable,
  noTopRadius,
  autoWidth,
}: TableProps) {
  const { loading: customLoading, data, queryResult } = useData<T, S>()
  const { pagination } = usePagination()
  const { error, loading: fetching } = queryResult
  const loading = useLoading(customLoading, fetching, data.length, queryResult.data)
  const { projections } = useProjections<T, S>()
  const header = useHeader<T, S>()

  const itemsPerPageRef = React.useRef(pagination.itemsPerPage)

  if (data.length > 0) {
    itemsPerPageRef.current = data.length
  }

  const showError = !!error
  const showEmptyColumns =
    projections.filter((projection) => projection.kind !== ProjectionKind.Decorative).length === 0
  const showEmptyState =
    !showEmptyColumns && !showError && !loading && !fetching && data.length === 0
  const showSkeleton =
    !showEmptyState &&
    !showError &&
    (loading || (pagination.kind === PAGINATION_KIND.OFFSET && data.length === 0))
  const showBody = !showEmptyColumns && !showError && !loading && data.length > 0
  const showPagination =
    !pagination.infiniteScroll &&
    !showEmptyColumns &&
    !showEmptyState &&
    !showError &&
    !customLoading

  useEffect(() => {
    // Only report the error if it's an Apollo error
    if (error?.name) {
      Sentry.captureException(error)
    }
  }, [error?.name])

  return (
    <TableComponent
      density={density}
      bottomSticky
      optionsBottom={
        showPagination ? () => <Pagination pagination={pagination} loading={fetching} /> : undefined
      }
      noTopRadius={noTopRadius}
      autoWidth={autoWidth}
    >
      {showEmptyColumns && (
        <EmptyState header={header} kind="projections" selectable={selectable} />
      )}
      <Head header={header} selectable={selectable} />
      {showError && <Error header={header} selectable={selectable} />}
      {showSkeleton && (
        <Skeleton header={header} selectable={selectable} itemsPerPage={itemsPerPageRef.current} />
      )}
      {showEmptyState && (
        <EmptyState kind="data" header={header} emptyState={emptyState} selectable={selectable} />
      )}
      {showBody && <Body header={header} loadingMore={fetching} selectable={selectable} />}
    </TableComponent>
  )
}

export default React.memo(Table)
